/* ============================================
   HN Select
   Custom element that progressively enhances a
   native <select>. The native element stays in the
   DOM (hidden, form-submittable) and receives a
   synthesized `change` event on selection so existing
   Shopify handlers (facet filters, variant picker,
   country pickers) continue to work unchanged.

   Usage:
     <hn-select>
       <select name="sort_by" id="SortBy">
         <option value="manual" selected>Featured</option>
         ...
       </select>
     </hn-select>
   ============================================ */

(function () {
  if (customElements.get('hn-select')) return;

  const CHEVRON_SVG =
    '<svg class="hn-select__chevron" aria-hidden="true" focusable="false" viewBox="0 0 12 8" fill="none">' +
    '<path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  const CHECK_SVG =
    '<svg class="hn-select__option-check" aria-hidden="true" focusable="false" viewBox="0 0 14 14" fill="none">' +
    '<path d="M2.5 7.5L6 11L11.5 4" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  let uidCounter = 0;

  class HnSelect extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.focusIndex = -1;
      this.selectedIndex = -1;
      this._typeBuffer = '';
      this._typeTimer = null;
      this._onDocClick = this._onDocClick.bind(this);
      this._onDocKeydown = this._onDocKeydown.bind(this);
      this._onNativeChange = this._onNativeChange.bind(this);
      this._onWinResize = this._onWinResize.bind(this);
    }

    connectedCallback() {
      this.native = this.querySelector(':scope > select');
      if (!this.native) return;
      if (this._initialized) return;
      this._initialized = true;

      this.native.classList.add('hn-select__native');
      // Keep the native select out of the keyboard tab order. The
      // trigger button becomes the user-facing tab stop; the native
      // remains in the DOM for form submission and assistive tech
      // (which finds it via the combobox role + aria-labelledby).
      this.native.setAttribute('tabindex', '-1');

      this._buildUI();
      this._bindEvents();
      this.syncFromNative();
      this._applyMinWidth();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick, true);
      document.removeEventListener('keydown', this._onDocKeydown, true);
      window.removeEventListener('resize', this._onWinResize);
      if (this.native) this.native.removeEventListener('change', this._onNativeChange);
    }

    refreshOptions() {
      if (!this.native || !this.listbox) return;
      this._renderOptions();
      this.syncFromNative();
      if (this.isOpen) this._positionListbox();
    }

    _buildUI() {
      const uid = ++uidCounter;
      this._triggerId = (this.native.id || 'hn-select-' + uid) + '-trigger';
      this._listboxId = (this.native.id || 'hn-select-' + uid) + '-listbox';

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'hn-select__trigger';
      this.trigger.id = this._triggerId;
      this.trigger.setAttribute('role', 'combobox');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', this._listboxId);

      const labelledBy = this._resolveLabelledBy();
      if (labelledBy) this.trigger.setAttribute('aria-labelledby', labelledBy);
      else if (this.native.getAttribute('aria-label')) {
        this.trigger.setAttribute('aria-label', this.native.getAttribute('aria-label'));
      }
      if (this.native.disabled) this.trigger.disabled = true;

      this.valueEl = document.createElement('span');
      this.valueEl.className = 'hn-select__value';
      this.trigger.appendChild(this.valueEl);
      this.trigger.insertAdjacentHTML('beforeend', CHEVRON_SVG);

      this.listbox = document.createElement('ul');
      this.listbox.className = 'hn-select__listbox';
      this.listbox.id = this._listboxId;
      this.listbox.setAttribute('role', 'listbox');
      this.listbox.hidden = true;

      this._renderOptions();

      this.appendChild(this.trigger);
      this.appendChild(this.listbox);
    }

    _resolveLabelledBy() {
      if (this.native.getAttribute('aria-labelledby')) return this.native.getAttribute('aria-labelledby');
      if (this.native.id) {
        const lbl = document.querySelector('label[for="' + this.native.id + '"]');
        if (lbl) {
          if (!lbl.id) lbl.id = this.native.id + '-label';
          return lbl.id;
        }
      }
      return null;
    }

    _renderOptions() {
      this.listbox.textContent = '';
      this.optionEls = [];
      const opts = Array.from(this.native.options);
      opts.forEach((opt, i) => {
        const item = document.createElement('li');
        item.className = 'hn-select__option';
        item.id = this._listboxId + '-option-' + i;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
        item.dataset.index = String(i);
        item.dataset.value = opt.value;
        if (opt.disabled) item.setAttribute('aria-disabled', 'true');

        // Optional picture per option (scent swatches): <option data-image="url">.
        const image = opt.getAttribute('data-image');
        if (image) {
          item.classList.add('hn-select__option--thumb');
          item.appendChild(this._createThumb(image, 'hn-select__option-thumb'));
        }

        const label = document.createElement('span');
        label.className = 'hn-select__option-label';
        label.textContent = opt.textContent.trim();
        item.appendChild(label);
        item.insertAdjacentHTML('beforeend', CHECK_SVG);

        this.listbox.appendChild(item);
        this.optionEls.push(item);
      });
      this.selectedIndex = this.native.selectedIndex;
    }

    _bindEvents() {
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
      this.trigger.addEventListener('keydown', (e) => this._onTriggerKeydown(e));

      this.listbox.addEventListener('click', (e) => {
        const item = e.target.closest('.hn-select__option');
        if (!item || !this.listbox.contains(item)) return;
        const idx = parseInt(item.dataset.index, 10);
        if (this._isOptionDisabled(idx)) return;
        this.selectIndex(idx);
        this.close();
        this.trigger.focus();
      });

      this.listbox.addEventListener('mousemove', (e) => {
        const item = e.target.closest('.hn-select__option');
        if (!item) return;
        const idx = parseInt(item.dataset.index, 10);
        if (!isNaN(idx)) this._focusOption(idx, false);
      });

      this.native.addEventListener('change', this._onNativeChange);
    }

    _onTriggerKeydown(e) {
      const key = e.key;
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault();
        this.open();
        this._focusOption(this.selectedIndex >= 0 ? this.selectedIndex : 0);
      } else if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        this.toggle();
        if (this.isOpen) this._focusOption(this.selectedIndex >= 0 ? this.selectedIndex : 0);
      } else if (key === 'Home') {
        e.preventDefault();
        this.open();
        this._focusOption(0);
      } else if (key === 'End') {
        e.preventDefault();
        this.open();
        this._focusOption(this.optionEls.length - 1);
      } else if (/^[\w]$/.test(key) && key.length === 1) {
        this._typeToSearch(key);
      }
    }

    _onDocKeydown(e) {
      if (!this.isOpen) return;
      const key = e.key;
      if (key === 'Escape') {
        e.preventDefault();
        this.close();
        this.trigger.focus();
      } else if (key === 'Tab') {
        this.close();
      } else if (key === 'ArrowDown') {
        e.preventDefault();
        this._focusOption(Math.min(this.focusIndex + 1, this.optionEls.length - 1));
      } else if (key === 'ArrowUp') {
        e.preventDefault();
        this._focusOption(Math.max(this.focusIndex - 1, 0));
      } else if (key === 'Home') {
        e.preventDefault();
        this._focusOption(0);
      } else if (key === 'End') {
        e.preventDefault();
        this._focusOption(this.optionEls.length - 1);
      } else if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        if (this.focusIndex >= 0 && !this._isOptionDisabled(this.focusIndex)) {
          this.selectIndex(this.focusIndex);
          this.close();
          this.trigger.focus();
        }
      } else if (/^[\w]$/.test(key) && key.length === 1) {
        this._typeToSearch(key);
      }
    }

    _onDocClick(e) {
      if (!this.contains(e.target)) this.close();
    }

    _onWinResize() {
      if (this.isOpen) this._positionListbox();
    }

    _onNativeChange() {
      this.syncFromNative();
    }

    _typeToSearch(char) {
      this._typeBuffer += char.toLowerCase();
      clearTimeout(this._typeTimer);
      this._typeTimer = setTimeout(() => { this._typeBuffer = ''; }, 500);
      const match = this.optionEls.findIndex((el) => {
        if (el.getAttribute('aria-disabled') === 'true') return false;
        const label = el.querySelector('.hn-select__option-label').textContent.trim().toLowerCase();
        return label.startsWith(this._typeBuffer);
      });
      if (match >= 0) {
        if (this.isOpen) this._focusOption(match);
        else this.selectIndex(match);
      }
    }

    _focusOption(idx, shouldScroll = true) {
      if (idx < 0 || idx >= this.optionEls.length) return;
      // Skip options that are hidden by CSS (e.g. the empty
      // placeholder option in the .field floating-label pattern,
      // hidden via .field .hn-select__option[data-value=""]).
      // offsetParent is null for display:none elements; this lets
      // keyboard navigation land on the first visible option even
      // when selectedIndex points to a hidden placeholder.
      while (idx < this.optionEls.length && this.optionEls[idx].offsetParent === null) {
        idx++;
      }
      if (idx >= this.optionEls.length) return;
      this.focusIndex = idx;
      this.optionEls.forEach((el, i) => el.classList.toggle('is-focused', i === idx));
      this.trigger.setAttribute('aria-activedescendant', this.optionEls[idx].id);
      if (shouldScroll) {
        const el = this.optionEls[idx];
        const box = this.listbox;
        const elTop = el.offsetTop;
        const elBottom = elTop + el.offsetHeight;
        if (elTop < box.scrollTop) box.scrollTop = elTop;
        else if (elBottom > box.scrollTop + box.clientHeight) {
          box.scrollTop = elBottom - box.clientHeight;
        }
      }
    }

    _isOptionDisabled(idx) {
      if (idx < 0 || idx >= this.optionEls.length) return true;
      return this.optionEls[idx].getAttribute('aria-disabled') === 'true';
    }

    selectIndex(idx) {
      if (this._isOptionDisabled(idx)) return;
      if (idx === this.selectedIndex) return;
      this.selectedIndex = idx;
      this.native.selectedIndex = idx;
      this._updateTriggerLabel();
      this.optionEls.forEach((el, i) => el.setAttribute('aria-selected', i === idx ? 'true' : 'false'));
      this.native.dispatchEvent(new Event('input', { bubbles: true }));
      this.native.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncFromNative() {
      const idx = this.native.selectedIndex;
      this.selectedIndex = idx;
      this._updateTriggerLabel();
      this.optionEls.forEach((el, i) => el.setAttribute('aria-selected', i === idx ? 'true' : 'false'));
    }

    _updateTriggerLabel() {
      const opt = this.native.options[this.selectedIndex];
      const text = opt ? opt.textContent.trim() : '';
      this.valueEl.textContent = text || this.native.getAttribute('data-placeholder') || 'Select';
      this.valueEl.classList.toggle('hn-select__value--placeholder', !text);

      const image = opt ? opt.getAttribute('data-image') : '';
      if (image) {
        if (!this.triggerThumb) {
          this.triggerThumb = this._createThumb(image, 'hn-select__trigger-thumb');
          this.trigger.insertBefore(this.triggerThumb, this.valueEl);
        } else {
          this.triggerThumb.src = image;
        }
      } else if (this.triggerThumb) {
        this.triggerThumb.remove();
        this.triggerThumb = null;
      }
      this.trigger.classList.toggle('hn-select__trigger--thumb', Boolean(image));
    }

    _createThumb(src, className) {
      const img = document.createElement('img');
      img.className = 'hn-select__thumb ' + className;
      img.src = src;
      img.alt = '';
      img.width = 32;
      img.height = 32;
      img.loading = 'lazy';
      img.setAttribute('aria-hidden', 'true');
      return img;
    }

    _applyMinWidth() {
      if (!this.native.options.length) return;
      const run = () => this._measureMinWidth();
      run();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    }

    _measureMinWidth() {
      const tCs = window.getComputedStyle(this.trigger);
      const probe = document.createElement('span');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText =
        'position:absolute;visibility:hidden;white-space:nowrap;top:-9999px;left:-9999px;pointer-events:none;';
      probe.style.fontFamily = tCs.fontFamily;
      probe.style.fontSize = tCs.fontSize;
      probe.style.fontWeight = tCs.fontWeight;
      probe.style.letterSpacing = tCs.letterSpacing;
      probe.style.textTransform = tCs.textTransform;
      document.body.appendChild(probe);

      let maxText = 0;
      Array.from(this.native.options).forEach((opt) => {
        probe.textContent = opt.textContent.trim();
        if (probe.offsetWidth > maxText) maxText = probe.offsetWidth;
      });
      probe.remove();

      // Trigger: just wide enough for its own chrome (padding + gap + chevron).
      //
      // Skipped for .hn-select--block, which is already stretched to 100% of
      // its container. There the content-derived min-width can only ever
      // OVERFLOW the parent: Happy Curves' long scent names ("Tahitian
      // Vanilla") measured 232px inside a 224px bundle-slot grid track, so
      // the trigger spilled over its column, swallowed the 0.6rem gap and
      // overlapped the neighbouring Size dropdown by ~2px. Because the value
      // is applied on upgrade, the gap was visible on first paint and then
      // collapsed — a JS mutation, not a CSS problem. Long labels ellipsize
      // instead, which is what the .hn-select__value overflow rules expect.
      //
      // .hn-select--fit sizes the trigger to the longest option as a WIDTH,
      // which CSS caps with max-width: 100% (a min-width would win over the
      // cap and spill out of a narrow column, as above).
      if (!this.classList.contains('hn-select--block')) {
        const tPadLeft = parseFloat(tCs.paddingLeft) || 0;
        const tPadRight = parseFloat(tCs.paddingRight) || 0;
        const tGap = parseFloat(tCs.columnGap) || parseFloat(tCs.gap) || 0;
        const chevronWidth = 12;
        const fitWidth = Math.ceil(maxText + tPadLeft + tPadRight + tGap + chevronWidth) + 'px';
        if (this.classList.contains('hn-select--fit')) this.trigger.style.width = fitWidth;
        else this.trigger.style.minWidth = fitWidth;
      }

      // Listbox: expand independently to fit longest option + its own chrome
      // (listbox padding + option padding + option gap + check icon)
      const opt = this.optionEls && this.optionEls[0];
      if (opt) {
        const optCs = window.getComputedStyle(opt);
        const lbCs = window.getComputedStyle(this.listbox);
        const check = opt.querySelector('.hn-select__option-check');
        const checkWidth = check ? parseFloat(window.getComputedStyle(check).width) || 0 : 0;
        let panelChrome =
          (parseFloat(lbCs.paddingLeft) || 0) +
          (parseFloat(lbCs.paddingRight) || 0) +
          (parseFloat(optCs.paddingLeft) || 0) +
          (parseFloat(optCs.paddingRight) || 0) +
          (parseFloat(optCs.columnGap) || parseFloat(optCs.gap) || 0) +
          checkWidth;
        // Options with a picture carry one more item and one more gap.
        const thumb = this.listbox.querySelector('.hn-select__option-thumb');
        if (thumb) {
          panelChrome +=
            (parseFloat(window.getComputedStyle(thumb).width) || 0) +
            (parseFloat(optCs.columnGap) || parseFloat(optCs.gap) || 0);
        }
        this.listbox.style.minWidth = Math.ceil(maxText + panelChrome) + 'px';
      }
    }

    /* The box the listbox can actually show in: the nearest scrolling or
       clipping ancestor (the cart drawer's item list, for one), else the
       viewport. Measuring the window alone let the list open downward
       into a scroll container's hidden overflow. */
    _clipRect() {
      let el = this.parentElement;
      while (el && el !== document.body && el !== document.documentElement) {
        const overflowY = window.getComputedStyle(el).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
          const r = el.getBoundingClientRect();
          return { top: Math.max(r.top, 0), bottom: Math.min(r.bottom, window.innerHeight) };
        }
        el = el.parentElement;
      }
      return { top: 0, bottom: window.innerHeight };
    }

    _positionListbox() {
      this.classList.remove('is-flipped');
      const rect = this.trigger.getBoundingClientRect();
      const clip = this._clipRect();
      const spaceBelow = clip.bottom - rect.bottom;
      const spaceAbove = rect.top - clip.top;
      const listboxHeight = Math.min(this.listbox.scrollHeight + 8, 320);
      if (spaceBelow < listboxHeight + 16 && spaceAbove > spaceBelow) {
        this.classList.add('is-flipped');
      }
    }

    open() {
      if (this.isOpen || this.native.disabled) return;
      this.isOpen = true;
      this.listbox.hidden = false;
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this._positionListbox();
      document.addEventListener('click', this._onDocClick, true);
      document.addEventListener('keydown', this._onDocKeydown, true);
      window.addEventListener('resize', this._onWinResize);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.listbox.hidden = true;
      this.classList.remove('is-open', 'is-flipped');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      this.focusIndex = -1;
      this.optionEls.forEach((el) => el.classList.remove('is-focused'));
      document.removeEventListener('click', this._onDocClick, true);
      document.removeEventListener('keydown', this._onDocKeydown, true);
      window.removeEventListener('resize', this._onWinResize);
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }
  }

  customElements.define('hn-select', HnSelect);
})();
